/**
 * AssignmentGradeModal — 결과표에서 배포본 하나를 열어 채점하는 창.
 *
 * 채점기를 새로 만들지 않는다. `AssignmentPanel` 이 이미 배포본 하나를 통째로 다루는 완결된
 * 화면(조회·채점·저장·마감·재오픈)이고, 그것을 그대로 감싼다. 두 진입이 같은 채점기를 쓴다:
 *
 *   학생 학습관리 → /exam-papers/assignment/{aid}  전체 화면 (그 화면에는 결과표가 없어 바탕이 없다)
 *   시험지 결과표 → [결과] 버튼                     이 모달 (표를 배경에 두고 여러 학생을 잇달아 본다)
 *
 * 껍데기가 맡는 것은 둘뿐이다 — **닫는 길**과 **미저장 확인**.
 *
 * 미저장 확인을 여기서 하는 이유: 모달은 [닫기](X)·배경 클릭·Esc 세 갈래로 닫히는데, 공통 Modal 이
 * 그 셋을 전부 `onClose` 하나로 모아 준다. 그래서 가드를 한 자리에만 두면 세 갈래가 함께 막힌다.
 * 채점기 자신의 [뒤로] 확인과 겹치지 않도록 `embedded` 로 그 버튼을 내린다.
 *
 * ⚠ **브라우저 뒤로가기는 이 가드를 지나지 않는다.** 주소가 먼저 바뀌고 이 창이 언마운트되므로
 * 물을 자리가 없다(react-router 6.22 의 useBlocker 는 데이터 라우터 전용이고 이 앱은
 * BrowserRouter 다). 그래서 채점기 안에 미저장 건수를 상시로 띄워 둔다 — 막지 못하는 대신
 * 무엇을 잃게 되는지는 늘 보이게 한다.
 */
import { useCallback, useRef, useState } from 'react';
import { Modal } from '../common';
import AssignmentPanel from './AssignmentPanel';

export default function AssignmentGradeModal({
  assignmentId,
  isOpen,
  onClose,
  onChanged = null,
  onDirtyChange = null,
}) {
  /**
   * 미저장 여부를 ref 로 든다. 이 값은 렌더에 쓰지 않고 **닫으려는 순간에만** 읽으므로,
   * state 로 들면 채점 한 번마다 모달 전체가 다시 그려진다(문항이 38개면 그 표가 통째로).
   */
  const dirtyRef = useRef(false);
  const onDirtyRef = useRef(onDirtyChange);
  onDirtyRef.current = onDirtyChange;

  const handleDirty = useCallback((d) => {
    dirtyRef.current = d;
    // 페이지의 이동 가드에도 그대로 올린다 — 모달이 떠 있는 동안 다른 시험지로 옮기는 길이 살아 있다
    onDirtyRef.current?.(d);
  }, []);

  const handleClose = useCallback(() => {
    if (dirtyRef.current) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('저장하지 않은 채점이 있습니다. 버리고 닫을까요?')) return;
    }
    // 닫으면 채점기가 사라진다 — 페이지 가드를 여기서 풀어야 다음 이동에서 헛되이 묻지 않는다.
    // (채점기도 언마운트되며 스스로 푼다. 먼저 푸는 쪽이 이기고 결과는 같다.)
    dirtyRef.current = false;
    onDirtyRef.current?.(false);
    onClose?.();
  }, [onClose]);

  /**
   * 머리 버튼 자리 — 채점기가 [새로고침]을 **닫기(X) 바로 왼쪽**에 그려 넣는 곳이다(오너 지시 2026-09-10).
   * 새로고침은 채점기의 일(미저장 확인·재조회·도는 아이콘)이라 여기서 다시 만들지 않고, 자리만 내준다.
   * 채점기가 포털로 이 칸에 버튼을 넣는다 — 콜백 ref 로 받아야 칸이 붙은 뒤 채점기가 다시 그린다.
   */
  const [headerSlot, setHeaderSlot] = useState(null);
  /**
   * 채점기 위에 겹이 떠 있는가(지금은 인쇄 미리보기) — 떠 있는 동안 이 창은 **inactive** 다.
   * 공통 Modal 은 Esc 를 document 에서 듣는다. 인쇄 오버레이는 body 에 따로 붙어 있어도 그 Esc 가
   * 여기까지 와서 채점 창이 함께 닫힌다. inactive 면 Esc·배경 클릭·초점 가두기가 멈춘다
   * (PaperComposeModal 이 미리보기·확인 창을 띄울 때 쓰는 것과 같은 길).
   */
  const [overlayOpen, setOverlayOpen] = useState(false);

  if (assignmentId == null) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="채점"
      size="xl"
      /**
       * 본문은 **스크롤하지 않는다.** 대신 세로 flex 로 서서 채점기에게 높이를 넘기고, 그 안에서
       * 표만 스크롤한다 — 진행률 막대가 표에 밀려 화면 밖으로 나가지 않게 하려는 것이다
       * (오너 지시 2026-09-06).
       *
       * ⚠ 공통 Modal 은 본문을 `flex-1 p-4 ${bodyClassName || 'overflow-y-auto'}` 로 조립한다
       * (Modal.js:253). 즉 **여기에 값을 주면 기본 세로 스크롤이 사라진다.** 그 성질을 알고 쓰는
       * 것이며, 아무 값이나 넘기면(예: 여백만 주려고 'p-4') 내용이 창 밖으로 흘러넘친다 —
       * 실제로 그렇게 한 번 나갔다. 여백은 Modal 이 이미 p-4 로 준다.
       */
      bodyClassName="flex min-h-0 flex-col overflow-hidden"
      preventScroll
      inactive={overlayOpen}
      headerActions={<span ref={setHeaderSlot} className="flex items-center" />}
    >
      <AssignmentPanel
        assignmentId={assignmentId}
        embedded
        onChanged={onChanged}
        onDirtyChange={handleDirty}
        headerSlot={headerSlot}
        onOverlayChange={setOverlayOpen}
      />
    </Modal>
  );
}
